<template>
  <div>
    <div class="mb-6 text-md text-bg-contractor">
      <span>{{ $t('textSuccess') }}</span>
      <span class="ml-4 text-text-label">(<a href="#" class=" hover:text-red underline transition duration-200"
                             @click.prevent="showDetails = !showDetails">{{ $t('showDetails') }}</a>)</span>

    </div>
    <div v-show="showDetails" class="bg-bg -mx-12 px-12 py-7 mb-6">
      <div class="border-b border-border pb-8">
        <div class="report-item">
          <div class="label">{{ $t('periodLabel') }}</div>
          <div class="val">
            {{ reportData.date_start }} — {{ reportData.date_end }}
          </div>
        </div>
      </div>
      <div v-if="!!reportData.withdrawals.length || !!reportData.replenishments.length"
           class="border-b border-border pb-8 pt-8 flex-items-center space-y-8">
        <div class="report-item" v-if="!!reportData.replenishments.length">
          <div class="label">{{ $t('refillLabel') }}</div>
          <div class="val">
            <div v-if="!!reportData.replenishmentsTotal.RUB">
              {{ displayCurrencyValue(reportData.replenishmentsTotal.RUB, getCurrencyByCode('RUB')) }}
            </div>
            <div v-if="!!reportData.replenishmentsTotal.USD">
              {{ displayCurrencyValue(reportData.replenishmentsTotal.USD, getCurrencyByCode('USD')) }}
            </div>
            <div v-if="!!reportData.replenishmentsTotal.EUR">
              {{ displayCurrencyValue(reportData.replenishmentsTotal.EUR, getCurrencyByCode('EUR')) }}
            </div>
          </div>
        </div>
        <div class="report-item" v-if="!!reportData.withdrawals.length">
          <div class="label">{{ $t('withdrawalsLabel') }}</div>
          <div class="val">
            <div v-if="!!reportData.withdrawalsTotal.RUB">
              {{ displayCurrencyValue(reportData.withdrawalsTotal.RUB, getCurrencyByCode('RUB')) }}
            </div>
            <div v-if="!!reportData.withdrawalsTotal.USD">
              {{ displayCurrencyValue(reportData.withdrawalsTotal.USD, getCurrencyByCode('USD')) }}
            </div>
            <div v-if="!!reportData.withdrawalsTotal.EUR">
              {{ displayCurrencyValue(reportData.withdrawalsTotal.EUR, getCurrencyByCode('EUR')) }}
            </div>
          </div>
        </div>
      </div>
      <div class="border-b border-border pb-8 pt-8">
        <div class="report-item">
          <div class="label">{{ $t('expensesLabel') }}</div>
          <div class="val commissions-grid">
            <div>{{ $t('taxesLabel') }}</div>
            <div>{{ displayCurrencyValue(reportData.taxes, portfolioCurrency) }}</div>
            <div>{{ $t('commissionsLabel') }}</div>
            <div>{{ displayCurrencyValue(reportData.commissions, portfolioCurrency) }}</div>
          </div>
        </div>
      </div>
      <div class="pt-8 pb-9 space-y-12">
        <div class="report-item" v-if="!!reportData.purchases.length">
          <div class="label">{{ $t('purchasesLabel') }}</div>
          <div class="val assets-grid col-gap-4 row-gap-4">
            <AssetIcon v-for="(purchase,i) in reportData.purchases" :key="'purchase-'+i"
                       :instrument="purchase.instrument"
                       v-tooltip="{ content: dealTooltip(purchase),placement:'top', classes: '--white' }"/>
          </div>
        </div>
        <div class="report-item" v-if="!!reportData.sales.length">
          <div class="label">{{ $t('salesLabel') }}</div>
          <div class="val assets-grid col-gap-4 row-gap-4">
            <AssetIcon v-for="(sale,i) in reportData.sales" :key="'sale-'+i"
                       :instrument="sale.instrument"
                       v-tooltip="{ content: dealTooltip(sale),placement:'top', classes: '--white' }"/>
          </div>
        </div>
        <div class="report-item" v-if="!!reportData.dividends.length">
          <div class="label">{{ $t('dividendsLabel') }}</div>
          <div class="val assets-grid col-gap-4 row-gap-4">
            <AssetIcon v-for="(dividend,i) in reportData.dividends" :key="'dividend-'+i"
                       :instrument="dividend.instrument"
                       v-tooltip="{ content: couponTooltip(dividend),placement:'top', classes: '--white' }"/>
          </div>
        </div>
        <div class="report-item" v-if="!!reportData.coupons.length">
          <div class="label">{{ $t('couponsLabel') }}</div>
          <div class="val assets-grid col-gap-4 row-gap-4">
            <AssetIcon v-for="(coupon,i) in reportData.coupons" :key="'coupon-'+i"
                       :instrument="coupon.instrument"
                       v-tooltip="{ content: couponTooltip(coupon),placement:'top', classes: '--white' }"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import AssetIcon from "@/components/cabinet/Investments/InvestmentAssets/InvestmentAssetItem/AssetIcon";

export default {
  name: "ImportPortfolioDetails",
  components: {AssetIcon},
  props: {
    reportData: {
      type: Object
    }
  },
  data() {
    return {
      showDetails: false
    }
  },
  computed: {
    ...mapGetters('investments/portfolios', ['portfolioCurrency']),
    ...mapGetters('cabinet/currencies', ['displayCurrencyValue', 'getCurrencyByCode']),
  },
  methods: {
    dealTooltip(deal) {
      let {ticker, amount, sum, currency} = deal,
          currencyObj = this.getCurrencyByCode(currency),
          displaySum = this.displayCurrencyValue(sum, currencyObj);
      return `<span class="font-bold">${ticker}</span> ${amount} ${this.$t('worth')} ${displaySum}`;
    },
    couponTooltip(coupon) {
      let {comment, sum, currency} = coupon,
          currencyObj = this.getCurrencyByCode(currency),
          displaySum = this.displayCurrencyValue(sum, currencyObj);
      return `<span class="font-bold">${comment}</span>  ${this.$t('worth')} ${displaySum}`;
    },
  },
  i18n: {
    messages: {
      en: {
        textSuccess: 'Brokerage report has been successfully uploaded and processed.',
        showDetails: 'View details',
        periodLabel: 'Report period:',
        refillLabel: 'Refill:',
        withdrawalsLabel: 'Withdrawals from account:',
        expensesLabel: 'Expenses:',
        taxesLabel: 'Taxes:',
        commissionsLabel: 'Commissions:',
        purchasesLabel: 'Purchases:',
        salesLabel: 'Sales:',
        dividendsLabel: 'Dividends:',
        couponsLabel: 'Coupons:',
        worth: 'worth',
      },
      ru: {
        textSuccess: 'Брокерский отчет успешно загружен и обработан.',
        showDetails: 'Посмотреть детали',
        periodLabel: 'Период отчета:',
        refillLabel: 'Пополнения счета:',
        withdrawalsLabel: 'Выводы со счета:',
        expensesLabel: 'Расходы:',
        taxesLabel: 'Налоги:',
        commissionsLabel: 'Коммиссии:',
        purchasesLabel: 'Покупки:',
        salesLabel: 'Продажи:',
        dividendsLabel: 'Дивиденды:',
        couponsLabel: 'Купоны:',
        worth: 'на сумму',
      }
    }
  }
}
</script>

<style scoped lang="scss">
.report-item {
  @apply grid text-text;
  grid-template-columns: 120px auto;
  grid-column-gap: 16px;

  .label {
    @apply text-sm leading-8;
  }

  .val {
    @apply text-md font-bold leading-8;
  }

  .commissions-grid {
    @apply grid;
    grid-template-columns: minmax(max-content, max-content) auto;
    grid-column-gap: 20px;
  }

  .assets-grid {
    @apply grid;
    grid-template-columns: repeat(25, 2.4rem);
  }
}
</style>